import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { isMaskedValue } from '../../../../../../../../utils/utils';
import {
    hasLinkedInEnrichment,
    LINKEDIN_ROW_FIELD,
    MANAGEMENT_PROFILE_MASKABLE_FIELDS,
    MANAGEMENT_PROFILE_ROW_TO_UW_FIELD,
    triggersLinkedInScrape,
} from '../../../../../../../../common/ManagementProfile/managementProfileFields';
import { MANAGEMENT_PROFILE_FIELD_VALIDATORS } from '../../../../../../../../common/ManagementProfile/managementProfileFieldValidators';
import {
    EditableDirector,
    EditableDirectorField,
    EditableDirectorValues,
    UseEditDirectorDetailsModalProps,
} from '../ManagementUserProfile.types';

export const EDITABLE_DIRECTOR_FIELDS = Object.keys(MANAGEMENT_PROFILE_ROW_TO_UW_FIELD) as EditableDirectorField[];

const buildInitialValues = (director: EditableDirector): EditableDirectorValues =>
    EDITABLE_DIRECTOR_FIELDS.reduce((values, field) => {
        const stored = director?.[field];
        const isMasked = MANAGEMENT_PROFILE_MASKABLE_FIELDS.has(field) && isMaskedValue(stored);
        values[field] = !stored || isMasked ? '' : stored;
        return values;
    }, {} as EditableDirectorValues);

export function useEditDirectorDetailsModal({ director, onSave, onClose, linkedinRefreshing = false }: UseEditDirectorDetailsModalProps) {
    const wasRefreshing = useRef(false);

    const [initialValues, setInitialValues] = useState<EditableDirectorValues>(() => buildInitialValues(director));
    const [values, setValues] = useState<EditableDirectorValues>(initialValues);
    const [errors, setErrors] = useState<Partial<Record<EditableDirectorField, string>>>({});
    const [saving, setSaving] = useState(false);
    const [confirmingLinkedInClear, setConfirmingLinkedInClear] = useState(false);

    useEffect(() => {
        if (wasRefreshing.current && !linkedinRefreshing) onClose();
        wasRefreshing.current = linkedinRefreshing;
    }, [linkedinRefreshing, onClose]);

    const setField = (field: EditableDirectorField, value: string) => {
        setValues((current) => ({ ...current, [field]: value }));
        setErrors((current) => (current[field] ? { ...current, [field]: undefined } : current));
    };

    // Only the fields being saved. Validating all five would block Save on a stored value the
    // user never touched — a legacy LinkedIn URL or a landline the API accepted before these
    // rules existed — with no way out except fixing an unrelated field first.
    const validateDirty = (dirty: Record<string, string>): boolean => {
        const nextErrors = Object.keys(dirty).reduce<Partial<Record<EditableDirectorField, string>>>((acc, field) => {
            const key = field as EditableDirectorField;
            const message = MANAGEMENT_PROFILE_FIELD_VALIDATORS[key]?.(values[key]);
            if (message) acc[key] = message;
            return acc;
        }, {});
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const dirtyUpdates = EDITABLE_DIRECTOR_FIELDS.reduce<Record<string, string>>((updates, field) => {
        const value = values[field].trim();
        if (value !== initialValues[field].trim()) updates[field] = value;
        return updates;
    }, {});

    const isSaveDisabled = saving || !Object.keys(dirtyUpdates).length || Object.values(errors).some(Boolean);

    /*
       Emptying the URL makes the backend drop the profile detail that URL produced, and that
       detail is not recoverable from the UI — so it needs consent, not just a save. Gated on
       enrichment actually existing: with nothing fetched there is nothing to warn about.
    */
    const clearsFetchedLinkedInData =
        LINKEDIN_ROW_FIELD in dirtyUpdates &&
        !dirtyUpdates[LINKEDIN_ROW_FIELD] &&
        hasLinkedInEnrichment(director?.customFields);

    const persist = async () => {
        setSaving(true);
        try {
            if (await onSave(dirtyUpdates)) {
                setInitialValues((current) => ({ ...current, ...dirtyUpdates }));
                // Held open only while a scrape is pending, so the user sees it land. A clear
                // starts no scrape, so it closes like any other save.
                if (!triggersLinkedInScrape(dirtyUpdates)) onClose();
            }
        } catch {
            // Never log the caught error — it can carry the request payload (phone/email/address).
            console.error('updateManagementProfile request failed');
            toast.error('Could not save. Please retry.');
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async () => {
        if (isSaveDisabled || !validateDirty(dirtyUpdates)) return;
        if (clearsFetchedLinkedInData) {
            setConfirmingLinkedInClear(true);
            return;
        }
        await persist();
    };

    const confirmLinkedInClear = async () => {
        setConfirmingLinkedInClear(false);
        await persist();
    };

    // Dismiss leaves the emptied field as the user typed it — the modal stays open so they can
    // put the URL back, and nothing has been sent.
    const dismissLinkedInClear = () => setConfirmingLinkedInClear(false);

    return {
        values,
        errors,
        saving,
        isSaveDisabled,
        setField,
        handleSave,
        confirmingLinkedInClear,
        confirmLinkedInClear,
        dismissLinkedInClear,
    };
}
